import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { applyTheme } from '../utils/theme-utils';

interface ThemeContextType {
  customColor: string;
  setCustomColor: (color: string) => void;
  isDark: boolean;
  setIsDark: (dark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const DEFAULT_COLOR = '#1976d2';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [customColor, setCustomColorState] = useState<string>(DEFAULT_COLOR);
  const [isDark, setIsDarkState] = useState<boolean>(false);

  useEffect(() => {
    try {
      const savedColor = localStorage.getItem('customThemeColor');
      const savedDark  = localStorage.getItem('isDarkMode') === 'true';

      const finalColor = savedColor || DEFAULT_COLOR;

      setCustomColorState(finalColor);
      setIsDarkState(savedDark);

      applyTheme(finalColor, savedDark);

    } catch(e) {
      console.error(e);
    }
  }, []);

  function setCustomColor(color: string) {
    setCustomColorState(color);
    localStorage.setItem('customThemeColor', color);
    applyTheme(color, isDark);
  }

  function setIsDark(dark: boolean) {
    setIsDarkState(dark);
    localStorage.setItem('isDarkMode', String(dark));
    applyTheme(customColor, dark);
  }

  return (
    <ThemeContext.Provider 
      value={{ customColor, setCustomColor, isDark, setIsDark }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
